import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { observe } from 'mobx';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import _ from 'lodash';
import {
  CurrentUserStore,
  ShopStore,
  CarStore,
  AppStore,
  TableStore,
} from 'stores';
import { UserRoles } from 'stores/Classes/UserObject';
import HeaderMessage from '../Report/HeaderMessage';

import { DealershipChooser } from 'components';
import { Row, Col, Typography, Button, notification, Spin, Modal } from 'antd';

import AssetTypeChooser from 'components/Vehicles/AssetTypeChooser';
import { first, isNil, set } from 'lodash';
import { Logger } from 'stores/Classes';
import VehicleNoteTextarea from 'components/Vehicles/VehicleNoteTextarea';
import { renderVehicleId } from 'containers/CarListTable/CarListTableColumns';
import RestoreAssetModal from 'components/Modals/RestoreAssetModal';
import TableContainer from 'components/Tables/TableContainer';
import LoadingWrapper from 'components/LoadingWrapper';
import CustomHeaderCell from 'shared/PitstopUI/PitstopTable/CustomHeaderCell';
import { CheckCircleOutlined } from '@ant-design/icons';

const Wrapper = styled.div`
  position: relative;
`;

const FullWidthRow = styled.tr`
  background: #e6f1ff;
  text-align: center;

  td {
    padding: 10px;
    color: #333;
  }
`;

const { Title } = Typography;

class ArchivedAssetsPage extends Component {
  static propTypes = {
    history: PropTypes.object,
  };

  componentDidMount() {
    AppStore.setSelectedSidebarKey('/archived-assets');

    if (CurrentUserStore.user.role !== UserRoles.customer) {
      if (!ShopStore.loaded && !ShopStore.pending) {
        ShopStore.getShops();
      }
    }

    this.init();
  }

  state = {
    archiveShopId: null,
    archivedAssets: [],
    initialLoading: false,
    isLoadingAssets: false,
    loadingAssetsCount: false,
    currentType: 'vehicles',
    assetsCountSummary: {
      countVehicles: 0,
      countTrailers: 0,
    },
    paginationMeta: {
      total: 0,
      current: 1,
    },
    selectedAssets: [],
    searchValue: '',
    isRestoreAssetsModalVisible: false,
    isRestoringAssets: false,
    allRecordsRowOption: true,
    selectAll: false,
    vehiclesApiUrl: '',
    isExportingCSV: false,
  };

  handleSearch = _.debounce(async (value) => {
    this.setState({
      searchValue: value,
      selectAll: false,
      allRecordsRowOption: false,
      selectedAssets: [],
    });
    try {
      await this.loadArchivedShopAssetsFromType(this.state.currentType, {
        offset: 0,
        limit: 10,
        filter: { carNameOrVinOrLicense: value },
      });
    } catch (error) {
      Logger.error(error);
    }
  }, 300);

  disposer = observe(ShopStore.currentShop, 'id', async () => {
    this.init();
  });

  vechilesTableStore = new TableStore();
  trailersTableStore = new TableStore();

  archivedAssetsColumns = [
    {
      key: 'car_name',
      onCell: () => ({
        className: 'hover-effect-cell',
      }),
      title: 'Unit ID',
      sorter: false,
      render: (car) => renderVehicleId(car, this.state.searchValue),
    },
    {
      key: 'archived_by',
      onCell: () => ({
        className: 'hover-effect-cell',
      }),
      title: 'Archived By',
      sorter: false,
      render: (unused, row) => {
        return row.extraDetails?.archive?.archivedBy || 'Unknown';
      },
    },
    {
      key: 'archived_on',
      onCell: () => ({
        className: 'hover-effect-cell',
      }),
      title: 'Archived On',
      sorter: false,
      render: (unused, row) => {
        const userTimezone =
          CurrentUserStore?.user?.settings?.timezone || 'America/Toronto';
        return row.extraDetails?.archive?.archiveDate
          ? moment(row.extraDetails?.archive?.archiveDate)
              .tz(userTimezone)
              .format('lll')
          : 'Unknown';
      },
    },
    {
      field: 'notes',
      key: 'notes',
      filter: false,
      onCell: () => ({
        className: 'hover-effect-cell',
      }),
      align: 'center',
      title: (
        <div>
          <span>Notes</span>
        </div>
      ),
      sorter: false,
      render: (car) => {
        return (
          <VehicleNoteTextarea
            car={car}
            onSave={({ id, notes }) => {
              this.saveCarNotes({ id, notes });
            }}
          />
        );
      },
      rowClick: false,
    },
  ];

  componentWillUnmount() {
    this.disposer();
  }

  loadArchiveShopTotalAssetsCount = async () => {
    try {
      this.setState({ loadingAssetsCount: true });
      const assetsCountSummary = await CarStore.getShopAssetsCountSummary(
        this.state.archiveShopId
      );
      this.setState({ assetsCountSummary: first(assetsCountSummary) || {} });
    } catch (error) {
      Logger.error(error);
      AppStore.addError(error.message || 'Failed to load assets count');
    } finally {
      this.setState({ loadingAssetsCount: false });
    }
  };

  saveCarNotes = async ({ id, notes }) => {
    try {
      await CarStore.updateCarNotes(id, notes);
    } catch (error) {
      AppStore.addError('Failed to save notes');
    }
  };

  handleRestoreAssets = async (notes) => {
    try {
      this.setState({ isRestoringAssets: true });
      await CarStore.restoreAssets({
        assetIds: this.state.selectedAssets,
        notes,
        shopId: this.state.archiveShopId,
      });
      this.setState(
        {
          isRestoreAssetsModalVisible: false,
          selectedAssets: [],
        },
        async () => {
          notification.success({
            message: 'Asset(s) Restored',
            description: 'Assets restored successfully',
            icon: <CheckCircleOutlined style={{ color: 'green' }} />,
          });
          await this.loadTotalAssetsCountAndArchivedShopAssets();
        }
      );
    } catch (error) {
      Logger.error(error);
      AppStore.addError(error.message || 'Failed to restore assets');
    } finally {
      this.setState({ isRestoringAssets: false });
    }
  };

  selectAll = () => {
    this.setState({
      selectAll: true,
      selectedAssets: this.state.archivedAssets.map((v) => v.id),
    });
  };

  clearSelection = () => {
    this.setState({
      selectAll: false,
      selectedAssets: [],
      allRecordsRowOption: false,
    });
  };

  convertQueryStringToObject = (queryString) => {
    const params = new URLSearchParams(queryString);
    const shopId = this.state.archiveShopId;
    const result = { offset: 0, shopId };

    for (const [key, value] of params.entries()) {
      if (value) {
        // Convert comma-separated values into arrays, or single values as strings
        result[key] = value.includes(',') ? value.split(',') : value;
      }
    }

    return result;
  };

  handleExportCSV = async () => {
    if (this.state.selectedAssets?.length === 0) {
      Logger.log('No items selected');
      return;
    }

    const {
      selectedAssets,
      archivedAssets,
      paginationMeta,
      selectAll,
      currentType,
    } = this.state;

    const pageTotal = paginationMeta.total;

    const params = this.convertQueryStringToObject(this.state.vehiclesApiUrl);
    params.limit = pageTotal;

    if (pageTotal > 10) {
      if (selectAll) {
        params.offset = 0;
        try {
          this.setState({ isExportingCSV: true });
          let result;
          switch (currentType) {
            case 'vehicles':
              result = await CarStore.getCarsForCSV(params);
              break;
            case 'trailers':
              params.trailers = true;
              result = await CarStore.getCarsForCSV(params);
              break;
            default:
              throw new Error('Invalid type');
          }
          const csvContent = this.convertToCSV(result.data, currentType);
          this.downloadCSV(csvContent);
        } catch (error) {
          Logger.log(error);
        } finally {
          this.setState({ isExportingCSV: false });
        }
      } else {
        Logger.log('Start exporting without calling Api!');
        this.startExportCSVWithoutCallingAPI(
          archivedAssets,
          selectedAssets,
          currentType
        );
      }
    } else {
      Logger.log('Start exporting without calling Api!');
      this.startExportCSVWithoutCallingAPI(
        archivedAssets,
        selectedAssets,
        currentType
      );
    }
  };
  startExportCSVWithoutCallingAPI = (currentVehicles, selectedAssets, type) => {
    const selectedData = currentVehicles.filter((car) =>
      selectedAssets.includes(car.id)
    );

    const csvContent = this.convertToCSV(selectedData, type);

    this.downloadCSV(csvContent);
  };

  convertToCSV = (data, type) => {
    let headers = [
      'Car Name',
      'Vin',
      'Telematic Device',
      ...(type === 'vehicles' ? ['Mileage', 'Engine Hours'] : []),
      'Archived By',
      'Archived On',
      'Notes',
    ];

    // Determine whether to use kilometers or miles
    const userSettings =
      CurrentUserStore.user?.settings?.odometer === 'km' ? 'km' : 'miles';

    const rows = data.map((car) => {
      const totalKmMi =
        userSettings === 'km'
          ? `${Math.round(Number(car.totalMileage))} km`
          : `${(Number(car.totalMileage) * 0.621371).toFixed(2)} mi`;

      const engineHours = Math.round(car.engineHours / 3600);
      const notes = car.notes ? `"${car.notes.replace(/"/g, '""')}"` : '--';
      let carName = car.carName || '--';
      let carVin = car.vin || '--';

      const device = car.scanner?.scannerId;
      let deviceManufacturer = device
        ? device.substring(0, device.indexOf(':'))
        : '--';
      let deviceId = device ? device.substring(device.indexOf(':') + 1) : '--';
      const carDevice = `${deviceManufacturer} - ${deviceId}`;

      let archivedBy = car.extraDetails?.archive?.archivedBy || 'Unknown';
      let archivedOn = car.extraDetails?.archive?.archiveDate || 'Unknown';

      return [
        carName,
        carVin,
        carDevice,
        ...(type === 'vehicles'
          ? [totalKmMi, `${engineHours} engine hours`]
          : []),
        archivedBy,
        archivedOn,
        notes,
      ];
    });

    const csvArray = [headers, ...rows];

    return csvArray.map((e) => e.join(',')).join('\n');
  };

  downloadCSV = (csvContent) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    const now = new Date();
    const timestamp = now.toISOString().replace(/[-:T]/g, '').split('.')[0];

    const filename = `exported_vehicles_data_${timestamp}.csv`;

    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  /**
   *
   * @param {string} type one of 'Vehicles' or 'Trailers'
   * @param {*} filters
   * @returns
   */
  loadArchivedShopAssetsFromType = async (type = 'vehicle', filters) => {
    try {
      if (!this.state.archiveShopId) {
        return;
      }
      this.setState({ isLoadingAssets: true });
      if (isNil(filters)) {
        filters = {
          offset: 0,
          limit: 10,
          sort: null,
          filter: {},
        };
      }

      set(filters, 'filter.exclude', 'issueList');
      set(
        filters,
        'filter.car_type',
        type === 'vehicle' || type === 'vehicles' ? 'Vehicle' : 'Trailer'
      );

      let response;

      const queryString = new URLSearchParams(filters.filter);
      if (filters.sort) queryString.set('sort', filters.sort);
      if (filters.offset) queryString.set('offset', filters.offset);
      if (filters.limit) queryString.set('limit', filters.limit);

      this.setState({ vehiclesApiUrl: queryString.toString() });

      const tableStore =
        type === 'vehicle' || type === 'vehicles'
          ? this.vechilesTableStore
          : this.trailersTableStore;

      if (CurrentUserStore.user.role === 'customer') {
        response = await CarStore.getUserCarsForTable(
          filters,
          tableStore,
          CurrentUserStore.user.id,
          this.state.archiveShopId,
          {
            isAddNextPmSummary: false,
            isAddWorkOrder: true,
            isAddEngineLightStatus: false,
            isAddDvirData: false,
            isReturnMetaAndData: true,
          }
        );
      } else {
        response = await CarStore.getShopCarsForTable(
          filters,
          tableStore,
          this.state.archiveShopId,
          {
            isAddNextPmSummary: false,
            isAddWorkOrder: true,
            isAddEngineLightStatus: false,
            isAddDvirData: false,
            isReturnMetaAndData: true,
          }
        );
      }

      if (!response) {
        return;
      }

      this.setState({
        archivedAssets: response.data.map((car) => ({
          ...car,
          key: car.id,
        })),
        paginationMeta: {
          ...this.state.paginationMeta,
          total: response.meta.count,
          current: filters.offset / 10 + 1,
        },
        selectedAssets: this.state.selectAll
          ? response.data.map((car) => car.id)
          : this.state.selectedAssets,
      });
    } catch (error) {
      Logger.error(error);
      AppStore.addError(error.message || 'Failed to load archived assets');
    } finally {
      this.setState({ isLoadingAssets: false });
    }
  };

  loadTotalAssetsCountAndArchivedShopAssets = async () => {
    this.loadArchiveShopTotalAssetsCount();
    await this.loadArchivedShopAssetsFromType(this.state.currentType, {
      offset: 0,
      limit: 10,
    });
  };

  async init() {
    const shopId = ShopStore.currentShop.id;
    try {
      this.setState({ initialLoading: true });
      const archiveShopId = await ShopStore.getShopArchiveShopId(shopId);
      if (archiveShopId) {
        // the shop has an archive shop
        return this.setState(
          {
            archiveShopId,
          },
          async () => {
            await this.loadTotalAssetsCountAndArchivedShopAssets();
          }
        );
      }
      this.setState({
        archivedAssets: [],
        archiveShopId: null,
      });
    } catch (error) {
      Logger.error(error);
    } finally {
      this.setState({ initialLoading: false });
    }
  }

  addFiltersToQuery = (type = '', value) => {
    if (!type) {
      throw new Error('Type is required');
    }
    const queryParams = new URLSearchParams(this.props.history.location.search);
    queryParams.set(type, value);
    this.props.history.push({
      search: queryParams.toString(),
    });
  };

  render() {
    if (this.state.initialLoading) {
      return (
        <LoadingWrapper>
          <Spin />
        </LoadingWrapper>
      );
    }

    const vehicleRowSelection = {
      selectedRowKeys: this.state.selectedAssets,
      onChange: (selectedRowKeys, selectedRows) => {
        this.setState({
          selectedAssets: selectedRowKeys,
        });
        if (selectedRows?.length < 10) {
          this.setState({
            selectAll: false,
            allRecordsRowOption: false,
          });
        } else {
          this.setState({
            allRecordsRowOption: true,
          });
        }
      },
      onSelectAll: (selected, selectedRows) => {
        this.setState({
          allRecordsRowOption:
            selectedRows?.length < this.state.paginationMeta.total && selected,
          selectAll: false,
        });
      },
      getCheckboxProps: (record) => ({
        // disabled: record.name === 'Disabled User', // Column configuration not to be checked
        // name: record.name,
      }),
    };

    return (
      <Wrapper>
        <Row>
          <Col span={24}>
            <Title>Archived Assets</Title>
          </Col>
        </Row>
        <Row>
          <Col span={16}>
            <DealershipChooser />
            <HeaderMessage />
          </Col>
        </Row>
        <Row
          style={{
            marginTop: '1rem',
          }}
        >
          <Col span={16}>
            <AssetTypeChooser
              disabled={this.state.isLoadingAssets}
              currentType={this.state.currentType}
              onChange={(type) => {
                this.setState(
                  {
                    currentType: type,
                    selectAll: false,
                    allRecordsRowOption: false,
                    selectedAssets: [],
                  },
                  async () => {
                    // set the current type to query param
                    this.addFiltersToQuery('currentType', type);
                    await this.loadArchivedShopAssetsFromType(
                      this.state.currentType,
                      {
                        offset: 0,
                        limit: 10,
                      }
                    );
                  }
                );
              }}
              loading={this.state.loadingAssetsCount}
              vehiclesCount={this.state.assetsCountSummary?.countVehicles || 0}
              trailersCount={this.state.assetsCountSummary?.countTrailers || 0}
            />
          </Col>
        </Row>
        <Row style={{ marginBottom: 10, marginTop: 10 }} gutter={[16, 16]}>
          <Col span={8}>
            <Input
              placeholder="Search by Unit ID, VIN or License Plate #"
              suffix={<SearchOutlined />}
              onChange={(e) => this.handleSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col
            span={8}
            style={{
              marginTop: 5,
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            <Button
              type="primary"
              data-test="restore-assets-button"
              size="small"
              disabled={this.state.selectedAssets.length === 0}
              style={{ marginRight: 10 }}
              onClick={() => {
                this.setState({
                  isRestoreAssetsModalVisible: true,
                });
              }}
            >
              Restore Asset(s)
            </Button>
            <Button
              type="primary"
              size="small"
              disabled={this.state.selectedAssets?.length === 0}
              style={{ marginRight: 10 }}
              onClick={this.handleExportCSV}
              data-test="export-csv-button"
            >
              Export CSV
            </Button>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <TableContainer
              data-test="archived-assets-table"
              columns={this.archivedAssetsColumns}
              dataSource={this.state.archivedAssets}
              loading={this.state.isLoadingAssets}
              rowSelection={vehicleRowSelection}
              pagination={{
                total: this.state.paginationMeta.total,
                current: this.state.paginationMeta.current,
                onChange: async (page) => {
                  await this.loadArchivedShopAssetsFromType(
                    this.state.currentType,
                    {
                      offset: (page - 1) * 10,
                      limit: 10,
                    }
                  );
                  // Reset selectAll when changing pages
                  if (this.state.selectAll) {
                    this.setState({
                      selectAll: false,
                      allRecordsRowOption: false,
                    });
                  }
                },
              }}
              components={{
                header: {
                  cell: CustomHeaderCell,
                },
                body: {
                  wrapper: (props) => (
                    <tbody {...props}>
                      {this.state.allRecordsRowOption &&
                        this.state.selectedAssets?.length === 10 && (
                          <FullWidthRow>
                            <td
                              colSpan={this.archivedAssetsColumns?.length + 1}
                            >
                              {this.state.selectAll ? (
                                <SelectAll
                                  totalCount={this.state.paginationMeta.total}
                                  onClearSelection={this.clearSelection}
                                />
                              ) : (
                                <SelectPartial
                                  selectedCount={
                                    this.state.selectedAssets?.length
                                  }
                                  totalCount={this.state.paginationMeta.total}
                                  onSelectAll={this.selectAll}
                                />
                              )}
                            </td>
                          </FullWidthRow>
                        )}
                      {props.children}
                    </tbody>
                  ),
                },
              }}
            />
          </Col>
        </Row>

        <RestoreAssetModal
          visible={this.state.isRestoreAssetsModalVisible}
          isRestoring={this.state.isRestoringAssets}
          onCancel={() => {
            this.setState({ isRestoreAssetsModalVisible: false });
          }}
          onOk={(notes) => {
            this.handleRestoreAssets(notes);
          }}
        />

        <Modal
          title="Exporting CSV..."
          open={this.state.isExportingCSV}
          footer={null}
          closable={false}
          centered
        >
          <div style={{ textAlign: 'center' }}>
            <Spin size="large" />
            <p>Processing your CSV export. Please wait...</p>
          </div>
        </Modal>
      </Wrapper>
    );
  }
}

export default withRouter(observer(ArchivedAssetsPage));

function SelectPartial({ selectedCount, totalCount, onSelectAll }) {
  return (
    <>
      {selectedCount} selected on this page.{' '}
      <a onClick={onSelectAll}>
        Select all {totalCount} applicable assets instead.
      </a>{' '}
      (Only assets matching the applied filters are available for selection)
    </>
  );
}

function SelectAll({ totalCount, onClearSelection }) {
  return (
    <>
      All {totalCount} assets in your fleet are selected.{' '}
      <a onClick={onClearSelection}>Clear selection</a>
    </>
  );
}
